export interface VisibilityCondition {
  variable: string
  operator: '==' | '!=' | '>' | '<' | '>=' | '<='
  value: number | string | boolean
}

export interface VisibilityRule {
  conditions: VisibilityCondition[]
  logic: 'and' | 'or'
}

export interface Variable {
  name: string
  type: 'slider' | 'input' | 'toggle' | 'steps' | 'dropdown' | 'tabs' | 'formula' | 'button'
  default: number | string | boolean
  min?: number
  max?: number
  step?: number
  formula?: string
}

export interface BlockAnimation {
  effect: 'fade' | 'vibrate' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'typing'
  duration: number
  delay: number
  trigger: 'on-appear' | 'on-variable-change'
  watchVariable?: string
}

export interface Block {
  id: string
  type: 'text' | 'slider' | 'chart' | 'button' | 'image' | 'steps' | 'chat_bubble' | 'codeblock' | 'toggle' | 'dropdown' | 'divider' | 'video' | 'tabs' | 'number_input' | 'compare' | 'poll' | 'quiz'
  content?: string
  variable?: string
  source?: string
  label?: string
  config?: Record<string, unknown>
  animation?: BlockAnimation
  accent?: string
  span?: 1 | 2 | 3
  visibility?: VisibilityRule
  background?: DemoBackground
}

export interface DemoBackground {
  type: 'solid' | 'gradient' | 'animated' | 'image'
  value: string
}

export interface DemoTheme {
  accent: string
  font?: string
}

export interface DemoContent {
  variables: Variable[]
  blocks: Block[]
  background?: DemoBackground
  theme?: DemoTheme
}

export interface DemoForkInfo {
  id: number
  title: string
  slug: string
  published: boolean
}

export interface Demo {
  id: number
  title: string
  slug: string
  description: string
  content: DemoContent
  published: boolean
  published_at: string | null
  created_at: string
  updated_at: string
  views_count?: number
  forks_count?: number
  forked_from?: DemoForkInfo
  categories?: string[]
  thumbnail_url?: string | null
  new_badges?: { slug: string; name: string; icon: string }[]
}

export interface Category {
  id: number
  name: string
  slug: string
}

export type BlockType = Block['type']

export interface BlockTypeGroup {
  label: string
  types: { type: BlockType; label: string; description: string }[]
}

export const BLOCK_TYPE_GROUPS: BlockTypeGroup[] = [
  {
    label: 'Content',
    types: [
      { type: 'text', label: 'Text', description: 'Dynamic text with variables' },
      { type: 'image', label: 'Image', description: 'Static illustration' },
      { type: 'compare', label: 'Before/After', description: 'Compare two images' },
      { type: 'video', label: 'Video', description: 'YouTube or Vimeo embed' },
      { type: 'divider', label: 'Divider', description: 'Separator or spacer' },
    ],
  },
  {
    label: 'Input',
    types: [
      { type: 'slider', label: 'Slider', description: 'Adjustable parameter' },
      { type: 'toggle', label: 'Toggle', description: 'On/off switch' },
      { type: 'dropdown', label: 'Dropdown', description: 'Multi-choice selector' },
      { type: 'tabs', label: 'Tabs', description: 'Tabbed content panels' },
      { type: 'number_input', label: 'Number Input', description: 'Direct number entry' },
    ],
  },
  {
    label: 'Data',
    types: [
      { type: 'chart', label: 'Chart', description: 'Interactive chart' },
      { type: 'codeblock', label: 'Code Block', description: 'Syntax-highlighted code' },
    ],
  },
  {
    label: 'Interactive',
    types: [
      { type: 'button', label: 'Button', description: 'Action trigger' },
      { type: 'steps', label: 'Steps', description: 'Step-by-step guide' },
      { type: 'chat_bubble', label: 'Chat Bubble', description: 'Conversation bubbles' },
      { type: 'poll', label: 'Poll', description: 'Vote & see results' },
      { type: 'quiz', label: 'Quiz', description: 'Multiple choice question' },
    ],
  },
]

// Flat list for backward compatibility
export const BLOCK_TYPES: { type: BlockType; label: string; description: string }[] =
  BLOCK_TYPE_GROUPS.flatMap((g) => g.types)

export const PREMIUM_BLOCK_TYPES: Set<BlockType> = new Set([
  'chart', 'slider', 'chat_bubble', 'codeblock', 'poll', 'quiz',
  'compare', 'toggle', 'dropdown', 'tabs', 'number_input',
])

export const BLOCKS_WITH_ACCENT: Set<BlockType> = new Set([
  'text', 'slider', 'chart', 'button', 'steps',
  'chat_bubble', 'toggle', 'dropdown', 'tabs', 'number_input', 'poll', 'quiz',
])
